/**
 * Reports Page - Phase 5 Implementation
 * Main Reports page with 3-column grid layout and report previews
 * Following SPARC methodology and existing component patterns
 */

import React, { useEffect, useState } from 'react';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  ReportCard, 
  ReportDetailDialog, 
  DateRangeSelector,
  ExportDropdown 
} from '../components/reports';
import {
  ReportsGridSkeleton,
  ReportsQuickStatsSkeleton,
  PageHeaderSkeleton
} from '../components/ui/skeleton-patterns';
import { 
  BarChart3, 
  TrendingUp, 
  IndianRupee, 
  FileText, 
  Package, 
  Users, 
  CreditCard,
  RefreshCw
} from 'lucide-react';
import { cn } from '../lib/utils';
import { useReportsStore } from '../stores/reportsStore';
import { ReportType } from '../types/reports';

const Reports: React.FC = () => {
  const {
    reports,
    reportMetadata,
    isLoading,
    fetchAllReports,
    fetchReportMetadata,
    refreshAllReports,
    setGlobalDateRange,
    exportMultipleReports,
    globalDateRange,
    openReportDialog,
    closeReportDialog,
    isDialogOpen,
    activeReport
  } = useReportsStore();
  const [isInitialLoading, setIsInitialLoading] = useState(true);

  // Initialize data on mount
  useEffect(() => {
    const initializeReports = async () => {
      setIsInitialLoading(true);
      try {
        // Fetch metadata and all reports in parallel
        await Promise.allSettled([
          fetchReportMetadata(),
          fetchAllReports()
        ]);
      } catch (error) {
        console.error('Failed to initialize reports:', error);
      } finally {
        // Add minimum loading time for better UX
        setTimeout(() => {
          setIsInitialLoading(false);
        }, 1000);
      }
    };

    initializeReports();
  }, [fetchReportMetadata, fetchAllReports]);

  const handleReportClick = (reportId: string) => {
    openReportDialog(reportId as ReportType);
  };

  const handleDialogClose = () => {
    closeReportDialog();
  };

  const handleRefresh = () => {
    refreshAllReports();
  };

  const handleExportAll = async () => {
    const allReportTypes = Object.keys(reportMetadata) as ReportType[];
    await exportMultipleReports(allReportTypes, {
      format: 'csv',
      includeCharts: false,
      includeMetadata: true,
      includeFilters: false,
      dateRange: globalDateRange
    });
  };

  if (isInitialLoading) {
    return (
      <DashboardLayout>
        <div className="flex-1 space-y-6 p-6">
          <PageHeaderSkeleton />
          <ReportsGridSkeleton />
          <ReportsQuickStatsSkeleton />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="flex-1 space-y-6 p-6">
        {/* Page Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Reports</h1>
            <p className="text-muted-foreground">
              Comprehensive business insights and analytics
            </p>
          </div>
          
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleRefresh}
              disabled={isLoading}
              className="flex items-center space-x-2"
            >
              <RefreshCw className={cn("h-4 w-4", isLoading && "animate-spin")} />
              <span>Refresh</span>
            </Button>
            
            <DateRangeSelector
              value={globalDateRange}
              onChange={setGlobalDateRange}
            />
            
            <ExportDropdown
              onExport={handleExportAll}
              label="Export All"
              reportType={null}
            />
          </div>
        </div>

        {/* Reports Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {Object.entries(reportMetadata).map(([reportType, metadata]) => {
            const report = reports[reportType as ReportType];
            // Enhance metadata with current data if available
            const enhancedMetadata = {
              ...metadata,
              previewValue: String(report?.summary?.keyMetrics?.primary || metadata.previewValue),
              trend: report?.summary?.trends?.primary || metadata.trend,
              lastUpdated: report?.lastUpdated 
                ? new Date(report.lastUpdated).toLocaleString()
                : metadata.lastUpdated
            };
            return (
              <ReportCard
                key={reportType}
                report={enhancedMetadata}
                isLoading={isLoading}
                onClick={() => handleReportClick(reportType)}
              />
            );
          })}
          
          {/* Add New Report Card */}
          <Card className="h-64 border-dashed border-2 hover:border-primary/50 transition-colors cursor-pointer">
            <CardContent className="flex flex-col items-center justify-center h-full space-y-4 text-muted-foreground">
              <div className="p-4 bg-muted/30 rounded-full">
                <BarChart3 className="h-8 w-8" />
              </div>
              <div className="text-center">
                <p className="font-medium">Create Custom Report</p>
                <p className="text-sm">Build your own analytics</p>
              </div>
              <Button variant="ghost" size="sm">
                Coming Soon
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Quick Stats Summary */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center space-x-2">
                <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
                  <TrendingUp className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Total Revenue</p>
                  <p className="text-lg font-semibold">
                    {reports['sales-dashboard']?.summary?.keyMetrics?.totalRevenue || '₹0'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center space-x-2">
                <div className="p-2 bg-green-100 dark:bg-green-900 rounded-lg">
                  <IndianRupee className="h-4 w-4 text-green-600 dark:text-green-400" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Net Profit</p>
                  <p className="text-lg font-semibold">
                    {reports['profit-loss']?.summary?.keyMetrics?.netProfit || '₹0'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center space-x-2">
                <div className="p-2 bg-orange-100 dark:bg-orange-900 rounded-lg">
                  <Package className="h-4 w-4 text-orange-600 dark:text-orange-400" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Inventory Value</p>
                  <p className="text-lg font-semibold">
                    {reports['inventory-status']?.summary?.keyMetrics?.totalValue || '₹0'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center space-x-2">
                <div className="p-2 bg-purple-100 dark:bg-purple-900 rounded-lg">
                  <Users className="h-4 w-4 text-purple-600 dark:text-purple-400" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Receivables</p>
                  <p className="text-lg font-semibold">
                    {reports['customer-outstanding']?.summary?.keyMetrics?.totalOutstanding || '₹0'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Report Detail Dialog */}
      <ReportDetailDialog
        reportType={activeReport}
        isOpen={isDialogOpen}
        onClose={handleDialogClose}
      />
    </DashboardLayout>
  );
};

export default Reports;